import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { PatientsService } from '../../../_services/patients/patients.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import IPatientHasTreatment from '../../../_interfaces/IPatientHasTreatment';
import IPatient from '../../../_interfaces/IPatients';
import { BaseComponent } from '../../base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { ButtonModule } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { CommonModule } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { CurrenciesService } from '../../../_services/currencies/currencies.service';

@Component({
  standalone: true,
  selector: 'app-form-edit-patient-treatment',
  templateUrl: './form-edit-patient-treatment.component.html',
  styleUrls: ['./form-edit-patient-treatment.component.scss'],
  imports: [
    ButtonModule,
    TranslatePipe,
    FormsModule,
    InputNumberModule,
    CommonModule,
    InputTextModule,
    InputTextarea,
  ],
})
export class FormEditPatientTreatmentComponent
  extends BaseComponent
  implements OnInit
{
  public onEdit: () => void = () => undefined;
  public treatment: IPatientHasTreatment = {} as IPatientHasTreatment;
  public patient: IPatient = {} as IPatient;
  public errors: { price?: string } = {};
  public isSaving = false;
  private currencySymbolByCode = new Map<string, string>();
  private currencySymbolById = new Map<number, string>();

  constructor(
    private patientsService: PatientsService,
    private messageService: MessageService,
    private dialogConf: DynamicDialogConfig,
    private translate: TranslateService,
    private errorsService: ErrorsService,
    private currenciesService: CurrenciesService,
    session: SessionService,
  ) {
    super(session);
  }

  private initializeData(): void {
    this.patient = this.dialogConf.data.patient;
    this.treatment = Object.assign({}, this.dialogConf.data.treatment);
    this.onEdit = this.dialogConf.data.onEdit;
    this.treatment.comment = this.treatment.comment || '';
  }

  private clearErrors(): void {
    this.errors = {};
  }

  get isPaid(): boolean {
    return (this.treatment?.payments || []).length > 0;
  }

  get currencyLabel(): string {
    const directSymbol = String(this.treatment?.currency?.symbol || '').trim();
    if (directSymbol) return directSymbol;

    const paymentCurrencyCode = String(
      this.treatment?.payments?.[0]?.site?.currency || '',
    )
      .trim()
      .toUpperCase();

    const codeCandidates = [
      this.treatment?.currency?.code,
      paymentCurrencyCode,
      this.treatment?.appointment?.calendar?.site?.currency,
      this.site?.currency,
      this.customer?.currency,
    ];

    for (const code of codeCandidates) {
      const symbol = this.getCurrencySymbolByCode(code);
      if (symbol) return symbol;
    }

    const customerCurrencyId = Number((this.customer as any)?.currencies_id || 0);
    if (customerCurrencyId > 0) {
      const symbolById = this.currencySymbolById.get(customerCurrencyId);
      if (symbolById) return symbolById;
    }

    const fallbackCode = codeCandidates
      .map((value) => String(value || '').trim().toUpperCase())
      .find(Boolean);
    return fallbackCode || '';
  }

  async updateTreatment(): Promise<void> {
    this.clearErrors();
    if (this.treatment?.price !== undefined && this.treatment?.price !== null) {
      const normalizedPrice = Number(this.treatment.price);
      if (!Number.isFinite(normalizedPrice) || normalizedPrice < 0) {
        this.errors.price = 'ERRORS.TREATMENT.PRICES.NEGATIVE';
        return;
      }
    }

    const payload: {
      comment?: string;
      price?: number;
    } = {
      comment: this.treatment?.comment || '',
    };

    if (!this.isPaid) {
      payload.price = Number(this.treatment?.price || 0);
    }

    this.isSaving = true;
    this.patientsService
      .updateTreatment(
        this.customers_id,
        this.patient.id,
        this.treatment.id,
        payload,
      )
      .subscribe({
        next: () => this.onTreatmentUpdated(),
        error: (error) => {
          this.isSaving = false;
          this.onTreatmentUpdateError(error);
        },
        complete: () => {
          this.isSaving = false;
        },
      });
  }

  private onTreatmentUpdated(): void {
    this.onEdit();
    this.showMessage(
      'MESSAGES.TREATMENTS.UPDATE.SUMMARY',
      'MESSAGES.TREATMENTS.UPDATE.DETAIL',
      { treatment: this.treatment?.treatment?.name || '' },
    );
  }

  private onTreatmentUpdateError(error: any): void {
    const fallbackCode = this.errorsService.getMessageFromTreatmentError(error);
    const backendMessages = Array.isArray(error?.error?.message)
      ? error.error.message
      : [error?.error?.message || fallbackCode || '500'];

    const detail = backendMessages
      .filter(Boolean)
      .map((message) => this.translateBackendCode(String(message)))
      .join('\n');

    this.messageService.add({
      severity: 'error',
      summary: this.translate.instant('MESSAGES.TREATMENTS.ERROR.SUMMARY'),
      detail:
        detail ||
        this.translate.instant(
          `MESSAGES.TREATMENTS.ERROR.${fallbackCode || '500'}`,
        ),
    });
  }

  private showMessage(
    summaryKey: string,
    detailKey: string,
    params: object = {},
  ): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translate.instant(summaryKey),
      detail: this.translate.instant(detailKey, params),
    });
  }

  private translateBackendCode(code: string): string {
    const normalized = String(code || '').trim();
    if (!normalized) return '';

    const candidate = `MESSAGES.TREATMENTS.ERROR.${normalized}`;
    const translated = this.translate.instant(candidate);
    if (translated !== candidate) return translated;

    if (normalized.startsWith('ERRORS.')) {
      const errorsKeyTranslation = this.translate.instant(normalized);
      if (errorsKeyTranslation !== normalized) return errorsKeyTranslation;
    }

    return normalized;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initializeData();
    this.loadCurrencyCatalog();
  }

  private loadCurrencyCatalog(): void {
    this.currenciesService.getAll().subscribe({
      next: (currencies) => {
        this.currencySymbolByCode.clear();
        this.currencySymbolById.clear();
        (currencies || []).forEach((currency) => {
          const code = String(currency?.code || '').trim().toUpperCase();
          const symbol = String(currency?.symbol || '').trim();
          if (code && symbol) {
            this.currencySymbolByCode.set(code, symbol);
          }
          const id = Number(currency?.id || 0);
          if (id > 0 && symbol) {
            this.currencySymbolById.set(id, symbol);
          }
        });
      },
    });
  }

  private getCurrencySymbolByCode(code?: string | null): string {
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return '';
    return this.currencySymbolByCode.get(normalized) || '';
  }
}
